import clsx from 'clsx';
import { useState, useRef, useEffect } from 'react';
import { BiChevronDown, BiChevronRight } from 'react-icons/bi';

import styles from './Accordion.module.scss';

type AccordionProps = {
  title: string;
  children: React.ReactNode;
  openedByDefault?: boolean;
  /**
   * Controls on the header row beside the title — a count, an all-or-none switch. They sit
   * outside the toggle button, so using one does not fold or unfold the section.
   */
  actions?: React.ReactNode;
  /** Where the fold arrow sits: after the title (default) or before it. */
  chevron?: 'start' | 'end';
  /** Indents the content to the header's own padding, so rows line up with the title. */
  inset?: boolean;
  className?: string;
};

export const Accordion = ({
  title,
  children,
  openedByDefault = false,
  actions,
  chevron = 'end',
  inset = false,
  className,
}: AccordionProps) => {
  const [open, setOpen] = useState(openedByDefault);
  const contentRef = useRef<HTMLDivElement>(null);

  const [height, setHeight] = useState<number | 'auto'>(openedByDefault ? 'auto' : 0);
  const isAutoRef = useRef(openedByDefault);
  const mountedRef = useRef(false);

  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      return;
    }
    if (!contentRef.current) return;
    if (open) {
      setHeight(contentRef.current.scrollHeight);
    } else {
      if (isAutoRef.current) {
        setHeight(contentRef.current.scrollHeight);
        requestAnimationFrame(() => requestAnimationFrame(() => setHeight(0)));
      } else {
        setHeight(0);
      }
    }
  }, [open]);

  const handleTransitionEnd = () => {
    if (open) {
      isAutoRef.current = true;
      setHeight('auto');
    } else {
      isAutoRef.current = false;
    }
  };

  const arrow = <span>{open ? <BiChevronDown /> : <BiChevronRight />}</span>;
  const label =
    chevron === 'start' ? (
      <span className={styles.accordionTitleStart}>
        {arrow}
        {title}
      </span>
    ) : (
      <>
        {title} {arrow}
      </>
    );

  return (
    <div className={clsx(styles.accordion, className)}>
      {actions ? (
        <div className={styles.accordionHeaderRow}>
          <button
            type="button"
            className={styles.accordionHeader}
            aria-expanded={open}
            onClick={() => setOpen((prev) => !prev)}
          >
            {label}
          </button>
          <div className={styles.accordionActions}>{actions}</div>
        </div>
      ) : (
        <button
          type="button"
          className={styles.accordionHeader}
          aria-expanded={open}
          onClick={() => setOpen((prev) => !prev)}
        >
          {label}
        </button>
      )}

      <div
        className={styles.accordionContent}
        style={{ height: height === 'auto' ? 'auto' : `${height}px` }}
        onTransitionEnd={handleTransitionEnd}
      >
        <div
          ref={contentRef}
          className={clsx(styles.accordionInner, inset && styles.accordionInset)}
        >
          {children}
        </div>
      </div>
    </div>
  );
};
